/**
 * This service is use for dataspaces call
 */

import axios, { type AxiosResponse } from 'axios';
import type {
  IntApplicationManagement,
  IntConnectorResult,
  IntConnectorTally,
  IntDataspaceMember,
  IntDataspaceParticipantDetails,
  IntInvitedParticipantResponse,
  IntParticipantRequest,
} from '../interfaces/build/IntBuildDataspaces';
import type {
  IntConnectorManagementPayload,
  IntConnectorManagementResponse,
  IntConnectorConfigurationResponse,
} from '@/modules/build/interfaces/IConnectorManagement';
import Util from '../util';
import { GroupInviteInput, InviteInput } from '@/modules/build/types/ParticipantInvite';


const buildService = {
  /**
   * Get user's owned dataspace
   */
  myOwnedDataspaces: () => {
    return axios.get(Util.getRestApiUrl('lila/dataspaces', 'dataspace'));
  },

  /**
   * Get particpant details
   */
  participantDetails: (dataspaceId: string) => {
    return axios.get(Util.getRestApiUrl(`lila/packageInstances/${dataspaceId}`, 'dataspace'));
  },

  /**
   * Get invited participant details
   */
  getInvitedParticipants: (dataspaceId: string, page: number, size: number): Promise<{ data: IntInvitedParticipantResponse, status: number }> => {
    return axios.get(Util.getRestApiUrl(`lila/inviteeList/${dataspaceId}?page=${page}&size=${size}`, 'dataspace'));
  },

  /**
   * Get incoming requests details
   */
  getIncomingRequests: (dataspaceId: string): Promise<{ data: IntParticipantRequest[], status: number }> => {
    return axios.get(Util.getRestApiUrl(`lila/requestConnectionToLiLaDs/${dataspaceId}`, 'dataspace'));
  },

  /**
   * Make a post call to approve or decline incoming request
   */
  updateApprovalStatusForIncomingRequest: (payload: {
    adminNotes: string
    requestStatus: string
  }, requestId: string): Promise<{ status: number }> => {
    return axios.post(Util.getRestApiUrl(`lila/requestConnectionToLiLaDs/act/${requestId}`, 'dataspace'), payload);
  },

  /**
   * Invite participant
   */
  inviteParticipant: (payload: {
    inviteeMailId: string
    dataspaceId: string
    organizationName: string
  }): Promise<{ data: string, status: number }> => {
    return axios.post(Util.getRestApiUrl('lila/inviteParticipant', 'dataspace'), payload);
  },

  /**
  * Get lila dataspace members
  */
  getDataSpaceMembers: (dataspaceId: string): Promise<{ data: IntDataspaceMember[], status: number }> => {
    return axios.get(Util.getRestApiUrl(`lila/members/${dataspaceId}`, 'dataspace'));
  },

  /**
  * Get connectors tally
  */
  getConnectorTally: (dataspaceId: string): Promise<{ data: IntConnectorTally, status: number }> => {
    return axios.get(Util.getRestApiUrl(`lila/connector-tally/${dataspaceId}`, 'dataspace'));
  },

  /**
  * Allocate connectors to Org
  */
  allocateConnectors: (payload: {
    dataspaceId: string
    organizationId?: string
    organizationName?: string
    requestConnectionToLiLaId?: string
    contactEmail?: string
    noOfConnectors: number
  }): Promise<{ status: number }> => {
    return axios.post(Util.getRestApiUrl('lila/deployConnectors', 'dataspace'), payload);
  },

  /**
   * Register new application
   */
  registerApplication: (payload: {
    dataspaceId: string
    ownerOrgId: string
    description: string
    dashboardUrl: string
    name: string
    logo?: string
  }): Promise<{ status: number }> => {
    return axios.post(Util.getRestApiUrl('lila/applications', 'dataspace'), payload);
  },

  /**
   * Upload image for register applications
   */
  uploadImageForRegisterApplications: (file: File): Promise<{ status: number, data: number }> => {
    const formData = new FormData();
    formData.append('logo', file);

    return axios.post(Util.getRestApiUrl('lila/application/uploadLogo', 'dataspace'), formData);
  },

  /**
   * Get dataspace details
   */
  dataspaceDetails: (dataspaceId: string) => {
    return axios.get(Util.getRestApiUrl(`lila/dataspaceDetails/${dataspaceId}`, 'dataspace'));
  },

  /**
   * Make a put call to update dataspace details
   */
  updateDataspaceDetails: (payload: {
    description?: string
    website?: string
  }, dataspaceId: string): Promise<{ status: number }> => {
    return axios.put(Util.getRestApiUrl(`lila/${dataspaceId}`, 'dataspace'), payload);
  },

  /**
   * Get service overview details
   */
  getServiceDetails: (dataspaceId: string) => {
    return axios.get(Util.getRestApiUrl(`lila/getServicesOverview/${dataspaceId}`, 'dataspace'));
  },

  /**
   * Get application management details
   */
  async getApplicationDetails (dataspaceId: string): Promise<IntApplicationManagement[]> {
    try {
      const response: AxiosResponse = await axios.get(Util.getRestApiUrl(`lila/applications/${dataspaceId}`, 'dataspace'));
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch applications');
    }
  },

  /**
   * Toggle dataspace visibility
   */
  toggleDataspaceVisbility (dataspaceId: string): Promise<{ status: number }> {
    return axios.put(Util.getRestApiUrl(`lila/toggleDataspaceVisibility/${dataspaceId}`, 'dataspace'));
  },

  /**
   * Connector health test api
   */
  intiateConnectorTest (payload: string[]): Promise<{ status: number }> {
    return axios.post(Util.getRestApiUrl('lila/test/connectors', 'dataspace'), payload);
  },

  /**
   * Connector test history details
   */
  getConnectorResults (dataspaceId: string): Promise<{ data: IntConnectorResult[], status: number }> {
    return axios.get(Util.getRestApiUrl(`lila/test/connectors/results/${dataspaceId}`, 'dataspace'));
  },

  /**
   * Update application details
   */
  updateApplicationDetails (payload: IntApplicationManagement, applicationId: string): Promise<{ status: number }> {
    return axios.put(Util.getRestApiUrl(`lila/applications/${applicationId}`, 'dataspace'), payload);
  },

  /**
   * Delete application
   */
  deleteApplication (applicationId: string): Promise<{ status: number }> {
    return axios.delete(Util.getRestApiUrl(`lila/applications/delete/${applicationId}`, 'dataspace'));
  },

  /**
   * Top Up Connector
   */
  topUpConnectors (dataspaceId: string): Promise<{ status: number }> {
    return axios.post(Util.getRestApiUrl(`lila/top-up-connector/${dataspaceId}`, 'dataspace'));
  },

  /**
  * Withdraw invitation
  */
  withdrawInvitation (inviteId: string): Promise<{ status: number }> {
    return axios.post(Util.getRestApiUrl(`lila/withDrawInvitation/${inviteId}`, 'dataspace'));
  },

  /**
   * Delete participant
   */
  deleteParticipant: (participantId: string, dataspaceId: string): Promise<{ status: number }> => {
    return axios.delete(Util.getRestApiUrl(`lila/${dataspaceId}/deletePackageInstance/${participantId}`, 'dataspace'));
  },

  resendInvitation (inviteId: string): Promise<{ status: number }> {
    return axios.post(Util.getRestApiUrl(`lila/reInvitePartner/${inviteId}`, 'dataspace'));
  },

  editParticipant: (dataspaceId: string, payload: {
    participantOrgId: String
    isDataspaceOperator: boolean
    isAnonymous: boolean
    dataspaceRole: string
  }): Promise<{ status: number }> => {
    return axios.put(Util.getRestApiUrl(`lila/update-role/${dataspaceId}`, 'dataspace'), payload);
  },

  getDataspaceSpecifications: (dataspaceId: string) : Promise<{ status: number, data: any }> => {
    return axios.get(Util.getRestApiUrl(`lila/getContractDetails/${dataspaceId}`, 'dataspace'));
  },

  sendParticipantInvites: (invites: InviteInput[]) => {
    return axios.post(Util.getRestApiUrl('lila/invitePartner', 'dataspace'), invites);
  },

  fetchWhitelistedIPs: (clusterId: string): Promise<{ status: number, data: { appliedIpWhitelist: string[], lastModifiedOn: string } }> => {
    return axios.get(Util.getRestApiUrl(`lila/fetchClusterWhitelistedIp?clusterId=${clusterId}`, 'dataspacev3'));
  },

  modifyWhitelistedIPs: (data: any): Promise<{ status: number }> => {
    return axios.post(Util.getRestApiUrl('lila/apply-cluster-ip-whitelist', 'dataspacev3'), data);
  },
  
  getConnectorDetails: (payload: IntConnectorManagementPayload, page: number, size: number): Promise<{ status: number, data: IntConnectorManagementResponse }> => {
    return axios.post(Util.getRestApiUrl(`connectors?page=${page}&size=${size}&showAllRecords=false`, 'dataspace'), payload);
  },

  getDataspaceParticipantDetails: (dataspaceId: string): Promise<{ status: number, data: IntDataspaceParticipantDetails[] }> => {
    return axios.get(Util.getRestApiUrl(`lila/features/group/groups/details/${dataspaceId}`, 'dataspace'));
  },

  getConfigurationDetails: (payload: IntConnectorManagementPayload): Promise<{ status: number, data: IntConnectorConfigurationResponse }> => {
    return axios.post(Util.getRestApiUrl('connectors/configuration', 'dataspace'), payload);
  },

  registerOnPremConnector: (payload: IntConnectorManagementPayload): Promise<{ status: number }> => {
    return axios.post(Util.getRestApiUrl('registerOnPremConnector', 'dataspace'), payload);
  },

  deleteConnector: (
    payload: IntConnectorManagementPayload,
    connectorId: string,
  ): Promise<{ status: number }> => {
    return axios.delete(
      Util.getRestApiUrl(`connectors/${connectorId}`, 'dataspace'),
      {
        data: payload,
      },
    );
  },

  editConnector: (connectorId: string, payload: IntConnectorManagementPayload): Promise<{ status: number }> => {
    return axios.put(Util.getRestApiUrl(`updateOnPremConnector/${connectorId}`, 'dataspace'), payload);
  },

  fetchConnectorConfiguration: (payload: IntConnectorManagementPayload, serviceAccId: string): Promise<{ status: number, data: IntConnectorConfigurationResponse }> => {
    return axios.post(Util.getRestApiUrl(`connectors/configuration/${serviceAccId}`, 'dataspace'), payload);
  },

  getExternalConnectorDetails: (dataspaceId: string): Promise<{ status: number, data: IntConnectorManagementResponse }> => {
    return axios.get(Util.getRestApiUrl('getAllExternalConnector?dataspaceId=' + dataspaceId, 'dataspace'));
  },

  fetchIDPName: (dataspaceId: string, orgId: string, groupId: string): Promise<{ status: number, data: string }> => {
    return axios.post(Util.getRestApiUrl(`lila/features/group/idp/getIdpNameByGroupId?dataSpaceId=${dataspaceId}&orgId=${orgId}&groupId=${groupId}`, 'dataspace'));
  },

  getGroupNames: (dataspaceId: string) => {
    return axios.get(Util.getRestApiUrl(`lila/features/group/groups/${dataspaceId}`, 'dataspace'));
  },

  getContactPersons: (groupId: string) => {
    return axios.get(Util.getRestApiUrl(`group/${groupId}/existingUsers?filter`, 'org'))
  },

  getAllContactPersons: (orgId: string) => {
    return axios.get(Util.getRestApiUrl(`user/org-admin/${orgId}`, 'org'))
  },

  inviteGroups: (payload: GroupInviteInput[]) => {
    return axios.post(Util.getRestApiUrl('lila/features/group/inviteGroup', 'dataspace'), payload);
  },

  getTechnicalUsers: (orgId: string) => {
    return axios.get(Util.getRestApiUrl(`lila/features/technicalintegration/clients/org/${orgId}`, 'dataspace'));
  },

  sendCredentialEmail: (clientId: string) => {
    return axios.get(Util.getRestApiUrl(`lila/features/technicalintegration/clients/${clientId}/sendCredentialEmail`, 'dataspace'));
  },
};

export default buildService;
